/**
 * RecipientSearchBar — shared client-side search overlay for the investor
 * recipient lists used by Drawdown emails / reminders / acknowledgments,
 * SOA email distribution, and Investor Communication.
 *
 * Stateless — the parent owns `value` and the filtered list. We just render
 * a small input + clear button so all senders share the same UX.
 */
import React from 'react';
import { Search, X } from 'lucide-react';

const RecipientSearchBar = ({
  value,
  onChange,
  placeholder = 'Search investor account, email, PAN…',
  testid = 'recipient-search',
  className = '',
}) => (
  <div className={`relative ${className}`}>
    <input
      type="text"
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full pl-7 pr-7 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
      data-testid={testid}
    />
    <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
    {value && (
      <button
        type="button"
        onClick={() => onChange('')}
        className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 p-1"
        data-testid={`${testid}-clear`}
        aria-label="Clear search"
      >
        <X size={11} />
      </button>
    )}
  </div>
);

/**
 * Keep the field-list lean & stable — we don't want a mistype like "label"
 * accidentally returning every row. Caller provides which keys to scan.
 */
export const filterByQuery = (rows, query, keys = ['account_name', 'email']) => {
  const q = (query || '').trim().toLowerCase();
  if (!q) return rows;
  return rows.filter(r => {
    const blob = keys
      .flatMap(k => {
        const v = r?.[k];
        if (Array.isArray(v)) return v;
        if (typeof v === 'string' || typeof v === 'number') return [String(v)];
        return [];
      })
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return blob.includes(q);
  });
};

export default RecipientSearchBar;
